// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 로그인 (한 화면 2단계) · 초대 수락 진입점
//
//  이메일 입력 → 코드 입력. **주소를 바꾸지 않고** 한 화면 안에서 단계만 바뀐다.
//  왜 한 화면인가: 코드 단계에서 "내가 어느 주소로 요청했더라" 를 확인할 수 있어야 한다.
//  주소가 바뀌면 뒤로 가기가 끼어들어 "코드를 받았는데 입력칸이 사라진" 상황이 생긴다.
//
//  ★ 비밀번호가 없는 설계다(DB-DESIGN §6-1). 그래서 **막힌 사람을 구할 방법이 화면에만
//    있다** — 연락처를 항상 띄워 두는 이유다(Contact.tsx 머리말).
//
//  ★★ `context` 로 **초대 전용 화면**이 된다.
//     App/router 에서 `/join` 을 `<Login context="invite" />` 로 그려라.
//     그냥 로그인 화면을 재사용하면 초대받은 사람이 첫 화면에서 막힌다 — 아래 §초대 참고.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Box, Card, Group, PinInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { ApiError, humanMessage, requestLoginCode, verifyLoginCode } from '../api/client';
import { useSession } from '../auth/SessionProvider';
import { Contact } from '../components/Contact';
import { CenteredShell } from '../components/Shell';
import { Notice, NoticeDetail } from '../components/Notice';
import { PrimaryButton } from '../components/Actions';
import { CODE_TTL_MIN, LOGIN_CODE_DIGITS, RESEND_COOLDOWN_SEC } from '../config';

type Step = 'email' | 'code';

/**
 * 이 화면이 왜 떠 있는가.
 *
 *   undefined      … 그냥 로그인
 *   'invite'       … 초대 링크를 눌러서 왔다 (`/join`, 확인 쿠키 있음)
 *   'invite-stale' … 초대 링크로 왔는데 **확인 쿠키가 없다**
 */
export type LoginContext = 'invite' | 'invite-stale';

// ═══ 탭이 폐기돼도 살아남게 ════════════════════════════════════════════════
//
//  ★★ 이게 없으면 **갤럭시에서 로그인이 막다른 길이 된다.**
//
//  실제 경로: 코드 단계 → 메일 앱으로 나감 → 8자리를 읽음(7±2 작업기억 한계 밖이다) →
//  브라우저로 돌아옴 → **Chrome 이 메모리 압박으로 탭을 폐기했으면 이 컴포넌트가
//  리마운트되고 step·email 이 useState 뿐이라 1단계로 되돌아간다.** 주소를 다시 치고
//  코드를 다시 받으려 하는데 **60초 쿨다운**이라 못 받는다. 그 사람은 거기서 끝난다.
//
//  ⚠️ localStorage 가 아니라 **sessionStorage** 다. 탭을 닫으면 지워져야 한다 —
//     공용 PC 에 "누가 어느 주소로 로그인하려 했는지" 를 남기지 않는다.
//  ⚠️ 코드 자체는 **저장하지 않는다.** 저장소에 남는 순간 그건 비밀번호와 같아진다.
const SS_KEY = 'cv.login';

interface Saved {
  step: Step;
  email: string;
  /** 「다시 보내기」가 풀리는 시각(epoch ms). 이걸 안 살리면 위 쿨다운 함정에 그대로 빠진다. */
  resendAt: number;
  /** 쿨다운이 걸린 주소. 제한은 주소마다 걸린다. */
  cooldownFor: string | null;
}

function loadSaved(): Saved | null {
  try {
    const raw = window.sessionStorage.getItem(SS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Saved>;
    if (v.step !== 'code' || typeof v.email !== 'string' || !v.email) return null;
    return {
      step: 'code',
      email: v.email,
      resendAt: typeof v.resendAt === 'number' ? v.resendAt : 0,
      cooldownFor: typeof v.cooldownFor === 'string' ? v.cooldownFor : null,
    };
  } catch {
    return null;
  }
}

function save(v: Saved | null): void {
  try {
    if (v) window.sessionStorage.setItem(SS_KEY, JSON.stringify(v));
    else window.sessionStorage.removeItem(SS_KEY);
  } catch {
    /* 사생활 보호 모드에서 막힐 수 있다. 저장 못 해도 로그인 자체는 되어야 한다. */
  }
}

// ═══ 코드칸 폭 ════════════════════════════════════════════════════════════
//
//  ★★★ 여기를 CSS 클래스로 옮기지 마라. **안 먹는다.** ★★★
//
//  Mantine PinInput 은 `--pin-input-size` 를 varsResolver 로 만들어
//  (`PinInput.mjs:31`) **인라인 style 속성**으로 내보낸다
//  (`get-style.mjs` 가 `...resolvedVars[selector]` 를 반환 style 객체에 펼친다).
//  **인라인 선언은 어떤 클래스 규칙도 이길 수 없다.** 옛 `global.css` 의 `.cv-pin` 은
//  그래서 **한 줄도 적용되지 않는 죽은 코드**였다 — 지웠다.
//  반대로 `style` **prop** 은 `get-style.mjs` 가 resolvedVars **다음에** 펼치므로 이긴다.
//
//  계산 (갤럭시 360px 기준):
//      쓸 수 있는 폭 = 360 − 24(Container md 좌우 12) − 32(Card lg 좌우 16) − 2(테두리)
//                    = 302px
//      칸 사이 간격  = gap 4px × 7 = 28px            ← ★ 옛 계산은 이걸 통째로 빠뜨렸다
//      칸 하나       = (100vw − 58 − 28) / 8
//  → 여유 2px 을 두고 90 을 뺀다.
//      360px → 33.75px (총 298 ≤ 302) ✓
//      320px → 28.75px (총 258 ≤ 262) ✓
//      442px 이상 → 44px 에서 멈춤
//  분기점이 아니라 **함수**라서 어떤 폭에서도 어긋나지 않는다.
//
//  ⚠️ gap 을 기본값(sm=8px)으로 되돌리면 간격만 56px 이 되어 360px 폰에서 다시 갈라진다.
//     갈라지면 「몇 자리인지」가 눈에 안 들어와서, 눈 나쁜 55세가 제일 처음 보는 화면이
//     5칸+3칸으로 부서진다.
const PIN_GAP = 4;
const PIN_STYLE = {
  '--pin-input-size': `min(2.75rem, calc((100vw - 90px) / 8))`,
} as CSSProperties;

// ═══ 화면 ═════════════════════════════════════════════════════════════════

export function Login({ context }: { context?: LoginContext } = {}) {
  const { reload } = useSession();

  const restored = useRef<Saved | null>(loadSaved());
  const [step, setStep] = useState<Step>(restored.current?.step ?? 'email');
  const [email, setEmail] = useState(restored.current?.email ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(
    restored.current ? '이어서 진행합니다. 받으신 코드를 넣어 주세요.' : null,
  );

  /**
   * 「다시 보내기」가 풀리는 **시각**(epoch ms). 남은 초를 상태로 들고 있지 않은 이유:
   *
   * ⚠️ setInterval 로 1씩 빼는 방식은 **폰에서 반드시 틀린다.** 모바일 브라우저는
   *    탭이 배경으로 가면 타이머를 늦추거나 아예 멈춘다. 그러면 화면을 다시 켰을 때
   *    "45초 남음" 에서 굳어 있고, 실제로는 이미 1분이 지났는데 버튼이 계속 잠겨 있다.
   *    → **시각을 저장하고 매 틱마다 Date.now() 로 다시 계산한다.**
   */
  const [resendAt, setResendAt] = useState<number>(restored.current?.resendAt ?? 0);
  const [now, setNow] = useState<number>(() => Date.now());
  const cooldownFor = useRef<string | null>(restored.current?.cooldownFor ?? null);

  /**
   * 코드 칸의 **첫 번째** input (Mantine PinInput 의 ref 는 0번 칸을 가리킨다).
   *
   * ⚠️ 코드가 틀려서 값을 지우면(setCode('')) 커서는 **마지막 칸에 그대로 남는다.**
   *    화면은 8칸이 다 비어 보이는데 실제 입력은 8번째 칸으로 들어가서, 사용자는
   *    "다시 쳤는데 아무 반응이 없다" 를 겪는다. [실측 2026-08-06]
   */
  const firstPin = useRef<HTMLInputElement>(null);

  const remaining = Math.max(0, Math.ceil((resendAt - now) / 1000));

  useEffect(() => {
    if (resendAt <= Date.now()) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [resendAt]);

  /**
   * 코드가 틀리면 커서를 **첫 칸으로** 되돌린다.
   *
   * ⚠️ 이걸 catch 안에서 바로 부르면 **동작하지 않는다.** [실측 2026-08-06]
   *    그 시점에는 setCode('') 가 아직 DOM 에 반영되지 않아 8칸이 다 차 있고,
   *    PinInput 의 focus 처리기가 "마지막으로 채워진 칸" 인 8번으로 커서를 도로 옮긴다.
   *    → 다시 그려진 **뒤에** 옮겨야 한다. 그래서 effect 다.
   */
  useEffect(() => {
    if (step === 'code' && error) firstPin.current?.focus();
  }, [step, error]);

  // ─ 코드 보내기 ────────────────────────────────────────────────────────
  async function sendCode(target: string, isResend: boolean) {
    const addr = target.trim().toLowerCase();
    if (!addr) {
      setError('이메일 주소를 입력해 주세요.');
      return;
    }

    // ★ 서버는 분당 1회 제한에 걸려도 **204** 를 준다(가입 여부를 숨기려고).
    //   즉 화면이 세지 않으면 사용자는 아무 반응 없는 버튼을 계속 누른다.
    //
    //   ⚠️ isResend 인지와 상관없이 막는다. 「다른 주소로」로 돌아가 **같은 주소**를 다시 넣는
    //      경로가 있는데, 그때도 서버는 코드를 안 보낸다. 여기서 안 막으면 화면만
    //      "코드를 보냈습니다" 라고 말하고 메일은 안 온다 — 제일 나쁜 거짓말이다.
    if (cooldownFor.current === addr && remaining > 0) {
      setStep('code');
      setError(null);
      setNotice(`이미 ${addr} 로 코드를 보냈습니다. ${remaining}초 뒤에 다시 보낼 수 있습니다.`);
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await requestLoginCode(addr);
      const at = Date.now() + RESEND_COOLDOWN_SEC * 1000;
      setEmail(addr);
      setStep('code');
      setCode('');
      cooldownFor.current = addr;
      setResendAt(at);
      setNow(Date.now());
      save({ step: 'code', email: addr, resendAt: at, cooldownFor: addr });
      setNotice(
        isResend
          ? '코드를 다시 보냈습니다. 먼저 온 코드는 더 이상 쓸 수 없습니다.'
          : '코드를 보냈습니다. 메일함을 확인해 주세요.',
      );
    } catch (e) {
      if (e instanceof ApiError && e.kind === 'bad_request') {
        // 400 은 **형식 오류뿐**이다(auth.ts:119). 형식은 서버에 묻지 않아도 알 수 있는 것이라
        // 400 을 줘도 가입 여부가 새지 않는다. 그래서 이 문구는 안전하다.
        setError('이메일 주소 형식을 확인해 주세요. (예: hong@company.co.kr)');
      } else if (e instanceof ApiError && e.status === 503) {
        // ★ 이 경로의 503 은 **서버에 CV_CODE_PEPPER 가 없다**는 뜻이다(apps/api README §4-4).
        //   DB 장애는 여기서 503 이 안 난다 — 응답을 먼저 보내고 뒤에서 처리하므로 204 다.
        setError('서버에 로그인 설정이 빠져 있습니다(관리자: apps/api 의 CV_CODE_PEPPER).');
      } else {
        setError(humanMessage(e));
      }
    } finally {
      setBusy(false);
    }
  }

  // ─ 코드 확인 ──────────────────────────────────────────────────────────
  async function verify(value: string) {
    if (value.length !== LOGIN_CODE_DIGITS || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await verifyLoginCode(email, value);
      // 들어왔으니 남은 흔적을 지운다. 안 지우면 로그아웃 뒤에 옛 단계가 되살아난다.
      save(null);
      // ★ 응답의 Set-Cookie 는 JS 가 못 읽는다(HttpOnly). "로그인됐다" 를 여기서 정하지 않고
      //   /api/me 를 다시 불러서 **서버에게 물어본다.** 그게 유일한 진실 원천이다.
      await reload();
      // 성공하면 App 이 알아서 다음 화면으로 갈아 끼운다 — 여기서 navigate 하지 않는다.
    } catch (e) {
      setCode(''); // 커서를 첫 칸으로 되돌리는 것은 위의 effect 가 한다(이유는 거기 주석에).
      if (e instanceof ApiError && e.kind === 'unauthorized') {
        // ⚠️ 서버가 사유를 **구분해 주지 않는다**. 틀린 코드·만료·이미 쓴 코드가
        //    전부 401 이다. 그러니 화면도 사유를 지어내지 말고 셋을 한 문장에 담는다.
        //    "코드가 틀렸습니다" 라고만 쓰면 만료된 사람이 같은 코드를 계속 다시 친다.
        // ⚠️⚠️ [2026-08-12 · db/023] **「시도 횟수를 넘겼습니다」를 여기서 뺐다.**
        //    코드가 몇 번 틀렸다고 죽는 일은 **없어졌다**(만료 10분 · 한 번 씀, 이 둘뿐이다).
        //    너무 많이 틀리면 401 이 아니라 **429** 가 오고 그건 아래 갈래가 받는다.
        //    이 문장에 남겨 두면 「새 코드를 받아라」로 잘못 이끈다 — 기다리면 되는 상황인데.
        setError(
          `코드가 맞지 않습니다. 틀렸거나, ${CODE_TTL_MIN}분이 지났거나, 이미 쓴 코드입니다. ` +
            '아래 「코드 다시 보내기」로 새 코드를 받아 주세요.',
        );
      } else if (e instanceof ApiError && e.status === 429) {
        // ⚠️ **여기서 「새 코드를 받으라」고 하면 안 된다.** 새 코드를 받아도 똑같이 막힌다 —
        //    막는 것은 코드가 아니라 **이 자리에서 틀린 횟수**이기 때문이다(db/023).
        //    할 일은 하나뿐이다: 기다린다. 그 사실을 그대로 말한다.
        setError('짧은 사이에 여러 번 틀렸습니다. 5분쯤 뒤에 다시 해 주세요. 코드는 그대로 쓰실 수 있습니다.');
      } else {
        setError(humanMessage(e));
      }
    } finally {
      setBusy(false);
    }
  }

  function backToEmail() {
    setStep('email');
    setCode('');
    setError(null);
    setNotice(null);
    save(null);
  }

  // ─ 그리기 ─────────────────────────────────────────────────────────────
  return (
    <CenteredShell>
      <Stack gap="lg" mt="lg">
        <Intro context={context} />

        <Card padding="lg">
          {step === 'email' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void sendCode(email, false);
              }}
            >
              <Stack gap="lg">
                <TextInput
                  label="이메일 주소"
                  description={
                    context
                      ? '초대장이 온 그 주소여야 합니다.'
                      : '회사 메일 주소를 넣어 주세요. 초대받은 주소여야 현장이 보입니다.'
                  }
                  placeholder="hong@company.co.kr"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  // type=email : 폰 자판에 @ 와 . 가 바로 뜬다
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  // 한글 자판이 켜져 있으면 주소를 못 친다. iOS 는 이 조합으로 영문 자판을 연다.
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                  disabled={busy}
                  required
                  // ⚠️ 흐린 글씨로 그리지 마라. 여기 적힌 주소가 이 화면의 핵심 데이터다.
                  styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
                />
                {/* 주동작은 카드 맨 아래 = 화면 아래쪽. 56px. */}
                <PrimaryButton type="submit" loading={busy}>
                  코드 받기
                </PrimaryButton>
              </Stack>
            </form>
          ) : (
            <Stack gap="lg">
              <Box>
                <Text size="md" fw={700}>
                  코드 입력
                </Text>
                {/* ★★ 주소를 **16px · ink · 고정폭**으로 그린다.
                    옛 코드는 `size="sm" c="dimmed"` 였다 = 14px muted = 야외 2.86:1.
                    이 화면의 논지 전체가 "내가 어느 주소로 요청했는지 대조하라" 인데
                    **대조 대상이 화면에서 가장 안 보이는 색**이었다. */}
                <Text size="md" fw={600} className="cv-mono" mt={4} style={{ wordBreak: 'break-all' }}>
                  {email}
                </Text>
                <Text size="sm" c="var(--cv-ink-2)" mt={4}>
                  위 주소로 보냈습니다. {CODE_TTL_MIN}분 안에 넣어 주세요.
                </Text>
              </Box>

              {/* ★ PinInput 인 이유:
                  ① **붙여넣기가 된다.** 사람들은 메일에서 코드를 긁어 붙인다. 칸이 나뉜
                     입력을 직접 만들면 이게 제일 먼저 깨지고, 깨진 걸 아무도 신고하지 않는다.
                  ② inputMode="numeric" → 폰에서 숫자 자판이 뜬다.
                  ③ oneTimeCode → autocomplete="one-time-code".
                     ⚠️ **"메일에서 자동으로 채워진다" 고 믿지 마라.** 안드로이드의
                        one-time-code 자동완성은 **SMS 경로**다(SMS Retriever / Autofill).
                        **메일로 온 코드는 갤럭시에서 자동으로 안 채워진다.** iOS 는 메일도
                        읽지만 이 제품의 주 사용자는 갤럭시다. 그래서 아래 붙여넣기 안내가
                        장식이 아니라 **주 경로**다.
                  ⚠️ length 를 숫자로 직접 쓰지 마라. LOGIN_CODE_DIGITS 하나만 본다(config.ts). */}
              <Group justify="center" gap={0}>
                <PinInput
                  ref={firstPin}
                  gap={PIN_GAP}
                  style={PIN_STYLE}
                  length={LOGIN_CODE_DIGITS}
                  type="number"
                  inputMode="numeric"
                  oneTimeCode
                  autoFocus
                  ariaLabel="로그인 코드"
                  value={code}
                  onChange={(v) => {
                    setCode(v);
                    // 다시 치기 시작하면 빨간 테두리를 걷는다. 고칠 기회를 주는 중에
                    // 계속 빨갛게 두면 "지금도 틀린 상태" 로 읽힌다.
                    if (error) setError(null);
                  }}
                  onComplete={(v) => void verify(v)}
                  disabled={busy}
                  error={!!error}
                />
              </Group>

              {/* ★ 55세는 PinInput 이 붙여넣기를 받는다는 걸 모른다. **말해 줘야 한다.**
                  8자리는 작업기억 한계(7±2) 밖이라 외워서 옮기는 것은 실패한다 —
                  장갑·햇빛·스트레스가 겹치면 더 그렇다. 16px 로 적는다. */}
              <Text size="md" c="var(--cv-ink-2)" ta="center">
                메일에서 코드를 복사해 <b>아무 칸이나 길게 눌러 붙여넣으셔도</b> 됩니다.
              </Text>

              <PrimaryButton
                onClick={() => void verify(code)}
                loading={busy}
                disabled={code.length !== LOGIN_CODE_DIGITS}
              >
                들어가기
              </PrimaryButton>

              {/* ★ 카운트다운. API 가 1분 내 재요청을 **조용히 무시**하는데(204 를 그대로 준다)
                  화면이 그걸 모르면 사용자는 반응 없는 버튼을 계속 누른다.

                  ⚠️ 남은 초를 **버튼 라벨 안에 넣지 마라.** 320px 폰에서 잘린다 —
                     실측으로 「코드 다시 보내기 (59초)」가 라벨 폭 190px 에 219px 을 그리며
                     **하필 남은 초 부분이** 잘렸다. 그러면 카운트다운이 있으나 마나다.
                     → 라벨은 짧게 두고 **초는 버튼 밖 자기 줄에** 둔다. 어떤 폭에서도 안 잘리고,
                       16px 이라 야외에서도 읽힌다. aria-describedby 로 버튼에 묶어
                       스크린리더도 "왜 못 누르는지" 를 듣는다. */}
              <Stack gap="sm">
                <PrimaryButton
                  variant="default"
                  onClick={() => void sendCode(email, true)}
                  disabled={busy || remaining > 0}
                  aria-describedby={remaining > 0 ? 'cv-resend-wait' : undefined}
                  style={{ minHeight: 48 }}
                >
                  코드 다시 보내기
                </PrimaryButton>

                {remaining > 0 ? (
                  // aria-live 를 걸지 않는다 — 걸면 스크린리더가 매 초 떠들어 나머지를 덮는다.
                  <Text id="cv-resend-wait" size="md" c="var(--cv-ink-2)" ta="center" className="cv-num">
                    {remaining}초 뒤에 다시 보낼 수 있습니다.
                  </Text>
                ) : null}

                <PrimaryButton
                  variant="subtle"
                  onClick={backToEmail}
                  disabled={busy}
                  style={{ minHeight: 48 }}
                >
                  다른 주소로
                </PrimaryButton>
              </Stack>
            </Stack>
          )}
        </Card>

        {/* ⚠️ `<Alert color="red">` 를 쓰지 마라 — Mantine 기본 red 는 4.51:1 로 AA 를
            0.01 로 넘긴다. Notice 가 우리 --cv-danger(6.44:1)로 그린다. Notice.tsx 머리말. */}
        {error ? <Notice tone="danger">{error}</Notice> : null}

        {notice && !error ? (
          <Notice tone="info">
            <Stack gap="xs">
              {/* ⚠️ "보냈습니다" 라고 단정하지 않는다. 서버는 **없는 주소에도 204** 를 준다
                  (가입 여부를 숨기려고). 즉 이 화면은 코드가 실제로 갔는지 모른다. */}
              <Text size="md">{notice}</Text>
              {/* ★ 옛 코드는 이 줄이 `size="xs" c="dimmed"` = 12px muted = 야외 2.86:1 ·
                  직사광 1.46:1 이었다. **가장 흔한 실패를 푸는 문장이 화면에서 가장 안 보이는
                  글자**였다. 14px · ink-2 로 올렸다(Notice.tsx 의 NoticeDetail). */}
              <NoticeDetail>
                메일이 안 보이면 <b>스팸함</b>을 확인해 주세요. 회사 메일이 걸러 내는 경우도
                있습니다. 초대받지 않은 주소에는 코드가 가지 않습니다.
              </NoticeDetail>
            </Stack>
          </Notice>
        ) : null}

        <Box>
          <Contact
            prefix="코드가 오지 않거나 주소를 모르겠다면"
            subject={context ? '[ClickView] 초대 링크 문의' : '[ClickView] 로그인/접속 문의'}
          />
          <Text size="sm" c="var(--cv-ink-2)" mt="md">
            이 시스템은 초대받은 사람만 씁니다. 로그인에 성공해도 초대받은 현장이 없으면
            아무것도 보이지 않습니다.
          </Text>
        </Box>
      </Stack>
    </CenteredShell>
  );
}

// ═══ §초대 · 화면 머리 ═════════════════════════════════════════════════════

/**
 * ★★★ `/join` 을 **로그인 화면과 다른 화면**으로 만드는 부분 ★★★
 *
 * 왜 필요한가 — 감리의 실제 경로를 분 단위로 따라가면 이렇게 된다:
 *
 *   09:12 메일의 초대 링크 탭 → `/join` → 로그인 안 됨 → **그냥 로그인 화면**
 *         · 「당진 고속도로」라는 글자가 없다 → 링크가 통했는지 아닌지 모른다
 *         · **어느 주소로 로그인해야 하는지 안 알려 준다** → 그는 주소를 서너 개 갖고 있다
 *           (회사·개인·감리단) → 기억나는 걸 넣는다 → 개인 Gmail
 *   09:13 코드 요청 → 09:19 코드 도착(회사 게이트웨이 지연) → 09:20 로그인 성공
 *         → 초대 수락 **실패** → 「이 초대를 열 수 없습니다」
 *
 *   **8분과 8자리 코드 하나를 태우고 막다른 화면에 도착한다.** 그리고 어느 주소가
 *   맞는지는 **여전히 모른다.** 전화한다. 안 받으면 안 쓴다.
 *
 * ★ 해법의 핵심 한 줄: **정답 주소는 이미 그의 폰에 열려 있다.**
 *   초대 메일이 **바로 그 주소로** 배달됐기 때문이다. 그러니 대상 주소를 서버에서
 *   내려 줄 필요(=열거 취약점 위험)가 없다. **가리키기만 하면 된다.**
 *   → 「방금 그 메일을 열어 보신 주소입니다」. DB 0줄, RPC 0개, 비용 0.
 *
 * ⚠️ 이걸 넣었으면 **메일 본문 맨 위에도** 받는 주소를 큰 글씨로 박아라:
 *      「이 초대는 lee@gam.co.kr 로 보냈습니다. 이 주소로 로그인해 주세요.」
 *    메일은 어차피 그 주소로 가므로 새는 정보가 0이고, 초대 실패 화면에서
 *    **가리킬 대상**이 생긴다. (메일 문안은 apps/api 쪽 일이라 여기서는 못 고친다.)
 */
function Intro({ context }: { context?: LoginContext }) {
  if (context === 'invite-stale') {
    // ★★ 사양이 `cv_inv` 쿠키 Max-Age=1800 만 정하고 **이 화면을 정의하지 않았다.**
    //    실제 경로: 09:12 링크 탭 → 09:14 레미콘 도착 → 현장 나감 → 12:30 복귀 →
    //    브라우저가 탭 복원 → `/join` → 쿠키 없음 → **빈 화면 또는 일반 로그인 화면.**
    //    그는 초대가 만료됐다고 결론 내린다. 실제로는 **14일이 남아 있다.**
    return (
      <Stack gap="md">
        <Title order={1}>초대 확인 시간이 지났습니다</Title>
        <Notice tone="warn">
          <Stack gap="xs">
            <Text size="md" fw={600}>
              메일의 초대 링크를 <b>한 번 더</b> 눌러 주세요.
            </Text>
            <NoticeDetail>
              이것만으로 그대로 이어집니다. <b>초대장 자체는 아직 살아 있습니다.</b>
              (초대는 보낸 날부터 14일간 유효합니다.)
            </NoticeDetail>
          </Stack>
        </Notice>
      </Stack>
    );
  }

  if (context === 'invite') {
    return (
      <Stack gap="md">
        {/* 28px. 「내가 누른 링크가 통했다」를 첫 줄에서 확인시킨다. */}
        <Title order={1}>초대를 받으셨습니다</Title>
        <Stack gap="xs" className="cv-measure">
          <Text size="md">들어가려면 로그인이 필요합니다.</Text>
          {/* ★ 이 두 줄이 F3(대상 주소를 못 보여 준다)을 통째로 우회한다.
              muted 금지 — 16px 굵게, ink. 여기가 안 읽히면 나머지가 다 무의미하다. */}
          <Text size="md" fw={700}>
            초대장이 온 메일 주소로 로그인하세요.
          </Text>
          <Text size="md" fw={600}>
            방금 그 메일을 열어 보신 주소입니다.
          </Text>
          <Text size="sm" c="var(--cv-ink-2)">
            주소가 기억나지 않으면 초대 메일의 <b>받는사람</b> 칸을 보세요. 메일 맨 위에도
            「이 초대는 ○○○ 로 보냈습니다」가 적혀 있습니다.
          </Text>
        </Stack>
      </Stack>
    );
  }

  return (
    <Box>
      {/* ★ 사람이 보는 이름은 **ClickView** 다 [2026-08-12]. 기반 시설 이름(저장소·Worker·
          버킷·DB)은 `clickviewer` 그대로 — 섞이지 않게 `brandContract.spec.ts` 가 지킨다. */}
      <Title order={1}>ClickView</Title>
      <Text size="md" c="var(--cv-ink-2)" mt={4}>
        비밀번호가 없습니다. 이메일로 받은 <b>{LOGIN_CODE_DIGITS}자리 숫자</b>로 들어갑니다.
      </Text>
    </Box>
  );
}
